<template>
  <standard-dialog
    v-model="dialog"
    :title="translate(formTitle)"
    :subtitle="editedCategory?.id ? `ID: ${editedCategory.id}` : ''"
    icon="mdi-folder-outline"
    @save="save"
    :disabled="!valid || isSaving"
    :loading="isSaving"
  >
    <v-form v-model="valid">
      <v-container class="container-padding">
        <v-row>
          <v-col cols="12">
            <text-field
              v-model="editedCategory.name"
              :rules="[rules.required]"
              :label="$t('title')"
              required
            />
          </v-col>
          <v-col cols="12">
            <autocomplete
              v-model="editedCategory.parent"
              :items="categories.filter((item) => item.id)"
              :label="$t('parent_group')"
              item-text="name"
              item-id="id"
              return-object
            />
          </v-col>
          <v-col cols="12" lg="6">
            <text-field v-model="editedCategory.code" :label="$t('code')" />
          </v-col>
          <v-col cols="12" lg="6">
            <text-field
              type="number"
              v-model="editedCategory.poredak"
              :label="$t('order_m')"
              :hint="$t('hi_num')"
              persistent-hint
              required
              :min="0"
              :rules="[rules.required]"
            />
          </v-col>
        </v-row>
      </v-container>
    </v-form>
  </standard-dialog>
</template>

<script>
import StandardDialog from "@/components/dialogs/StandardDialog.vue";
import categoryService from "@/services/category";
import hasValidation from "@/mixins/hasValidation";
import hasSnackbar from "@/mixins/hasSnackbar";

export default {
  name: "CategoryDialog",
  mixins: [hasValidation, hasSnackbar],
  components: {
    StandardDialog,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    categories: {
      type: Array,
      default: () => [],
    },
    category: {
      type: Object,
      default: () => ({
        name: "",
        code: "",
        poredak: null,
        parent: null,
      }),
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      editedCategory: {
        name: "",
        code: "",
        poredak: null,
        parent: null,
      },
      isSaving: false,
    };
  },
  computed: {
    dialog: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      },
    },
    formTitle() {
      return !this.isEdit ? this.$t("new_g") : this.$t("edit_g");
    },
  },
  watch: {
    value(newVal) {
      if (newVal) {
        this.editedCategory = Object.assign({}, this.category);
      }
    },
    category: {
      handler(newCategory) {
        this.editedCategory = Object.assign({}, newCategory);
      },
      deep: true,
    },
  },
  methods: {
    close() {
      this.$emit("input", false);
      this.$emit("close");
    },
    async save() {
      if (this.isSaving) return;

      this.isSaving = true;

      try {
        // Map display field names back to API field names (no mapping needed now)
        this.editedCategory.parent_id = this.editedCategory.parent
          ? this.editedCategory.parent.id
          : 0;

        if (this.isEdit) {
          await categoryService.updateCategory(
            this.editedCategory.id,
            this.editedCategory
          );
          this.showSuccessSnackbar(this.$t("category_updated"));
          this.$emit("category-updated", this.editedCategory);
          this.close();
        } else {
          const res = await categoryService.createCategory(this.editedCategory);
          this.editedCategory.id = res.data.id;
          this.showSuccessSnackbar(this.$t("category_created"));
          this.$emit("category-created", this.editedCategory);
          this.close();
        }
      } catch (error) {
        this.showErrorSnackbar(
          this.$t("error_saving_category") || error?.response?.data?.message
        );
        console.error("Error saving category:", error);
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>
